import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { getTopics } from "../../../store/topicSlice";
import { Link } from "react-router-dom";
import { Button } from "@mui/material";
import { BottomAcc, Check, Exit, FileWord, NoCheck, TopAcc, Video } from "../../../ui/icons";
import { useTranslationService } from '../../../utils/translate';
import { Certification, Download, Info, Phone, Vector } from '../../../ui/icons';
import { getMainVideo } from "../../../store/courseSlice";

const SidebarWrapper = styled.div`
    background: #0A1F44;
    padding: 24px;
    border-radius: 12px;
`;

const BuyButton = styled.button`
    background: #8C1C1C;
    color: #FFF;
    border-radius: 12px;
    padding: 8px 0;
    width: 100%;
    margin-top: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.3s;

    &:hover {
        background: #8C1C1C12;
    }
`;

const TextWrapper = styled.p`
    gap: 5px;
    display: flex;
    align-items: start;
`;

const TextIcon = styled.span`
    font-size: 15px;
    font-weight: 400;
    color: #FFFFFF;
    opacity: 0.8;
`;

const ButtonAcc = styled.button`
    @media (max-width: 1032px) {
        display: block;
        flex-direction: column;
    }
`;

const SideBar = ({ setVideo, setIdTopic }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { token } = useSelector((state) => state.auth);
    const { mainVideo } = useSelector((state) => state.courses);
    const { token: registerToken } = useSelector(state => state.form);
    
    const { translateText } = useTranslationService();
    const [checked, setChecked] = useState('');
    const { items, loading, error } = useSelector((state) => state.topics);
    const [openAccordion, setOpenAccordion] = useState(0);
    const [translatedTitles, setTranslatedTitles] = useState({});
    
    useEffect(() => {
        dispatch(getMainVideo())
    }, [dispatch]);

    const courseIncludes = [
        { icon: <Download />, text: t('course_details.lessons', { count: 6 }) },
        { icon: <Vector />, text: t('course_details.next_lesson', { date: '17.12.2024', time: '19:00' }) },
        { icon: <Phone />, text: t('course_details.mobile_access') },
        { icon: <Certification />, text: t('course_details.certificate') }
    ];

    // Определяем цены в зависимости от языка
    const currentPrice = i18n.language === 'ru' 
        ? mainVideo?.tenge_price 
        : mainVideo?.new_price;
    
    const oldPrice = i18n.language === 'ru' 
        ? mainVideo?.tenge_price - (mainVideo?.tenge_price * (mainVideo?.discount_percentage / 100))
        : mainVideo?.old_price;

    const toggleAccordion = (index) => {
        setOpenAccordion(openAccordion === index ? null : index);
    };

    useEffect(() => {
        dispatch(getTopics());
    }, [dispatch]);

    useEffect(() => {
        if (items.length > 0) {
            const topicWithLectures = items.find(item => item.topic.lectures.length > 0);
            
            if (topicWithLectures) {
                const firstLecture = topicWithLectures.topic.lectures[0];
                const valueToCheck = firstLecture.video || firstLecture.id;
                
                setChecked(valueToCheck);
                setVideo(firstLecture.video);
                setIdTopic(firstLecture);
            }
        }
    }, [items, setVideo, setIdTopic]);

    const translateItemTitle = async (item) => {
        if (i18n.language === 'en') return item.topic.title;
        
        try {
            const translated = await translateText(item.topic.title, i18n.language);
            return translated;
        } catch (error) {
            console.error('Translation error:', error);
            return item.topic.title;
        }
    };

    useEffect(() => {
        const translateAllTitles = async () => {
            const translations = {};
            for (const item of items) {
                translations[item.topic.id] = await translateItemTitle(item);
            }
            setTranslatedTitles(translations);
        };

        if (items.length > 0) {
            translateAllTitles();
        }
    }, [items, i18n.language]);

    if (loading) return <p>{t('common.loading')}</p>;
    if (error) return (
        <SidebarWrapper>
            <p className='font-normal text-[14px] text-[#EAEAEA]'>{t('course_details.full_course')}</p>
            <h2 className='font-semibold text-[36px] flex items-center gap-3 text-white'>
                {t('currency')}{Math.round(currentPrice) || 0}
                <span className='line-through text-[17px] text-[#EAEAEA] opacity-80 text-sm'>
                    {t('currency')}{Math.round(oldPrice) || 0}
                </span>
            </h2>
            <Link to={token || registerToken ? '/passingcourse/1' : '/signin'}>
                <BuyButton className='text-base'>{t('course_details.buy')}</BuyButton>
            </Link>
            <p className='opacity-80 text-[#EAEAEA] pt-2'></p>

            <h3 className='py-3 font-semibold text-white'>{t('course_details.eligibility')}</h3>
            <TextWrapper>
                <Info />
                <TextIcon>
                    <p className='text-[#EAEAEA]'>{t('course_details.eligibility_description')}</p>
                </TextIcon>
            </TextWrapper>

            <div className="mt-6">
                <h3 className='py-3 font-semibold text-white'>{t('course_details.includes')}</h3>
                <div className="space-y-3">
                    {courseIncludes.map((item, index) => (
                        <TextWrapper key={index}>
                            {item.icon}
                            <TextIcon style={{color: "#EAEAEA !important"}}>{item.text}</TextIcon>
                        </TextWrapper>
                    ))}
                </div>
            </div>
        </SidebarWrapper>
    );

    return (
        <div className='w-[100%]'>
            <div className='flex items-center justify-between pt-[20px] sm:pt-0 text-[20px] px-0 sm:px-4'>
                <h2 className='text-lg font-semibold py-2'>{t('sidebar.inaugural_lecture')}</h2>
                <div className='hidden sm:block'>
                    <Exit />
                </div>
            </div>
            <div className='rounded-xl sm:rounded-none bg-[#f7f7f7]' style={{
                height: "656px",
                overflowX: "scroll"
            }}>
                {items.map((item, index) => (
                    <div key={index}>
                        <ButtonAcc
                            onClick={() => toggleAccordion(item?.is_open ? item.topic.id : "")}
                            style={{ border: '1px solid #e1e1e1' }}
                            className={`flex flex-col text-sm py-4 px-4 text-left bg-[#f7f7f7] focus:outline-none justify-between w-full rounded-none sm:rounded-none`}
                        >
                            <div className="flex items-center justify-between w-full">
                                <div className='flex gap-[10px]'>
                                    {!item.is_open && (
                                        <div>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10"
                                                    stroke="#292D32"
                                                    strokeWidth="1.5" strokeLinecap="round"
                                                    strokeLinejoin="round"/>
                                                <path
                                                    d="M12 18.5C13.3807 18.5 14.5 17.3807 14.5 16C14.5 14.6193 13.3807 13.5 12 13.5C10.6193 13.5 9.5 14.6193 9.5 16C9.5 17.3807 10.6193 18.5 12 18.5Z"
                                                    stroke="#292D32" strokeWidth="1.5" strokeLinecap="round"
                                                    strokeLinejoin="round"/>
                                                <path
                                                    d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z"
                                                    stroke="#292D32" strokeWidth="1.5" strokeLinecap="round"
                                                    strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-medium">
                                            {translatedTitles[item.topic.id] || item.topic.title}
                                        </p>
                                        <div className='flex items-center gap-[6px] w-full'>
                                            <span>
                                                <svg width="3" height="4" viewBox="0 0 3 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <circle opacity="0.7" cx="1.5" cy="2" r="1.5" fill="#1E1E1E" fillOpacity="0.3"/>
                                                </svg>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    {openAccordion === item?.topic?.id ? <TopAcc /> : <BottomAcc />}
                                </div>
                            </div>
                        </ButtonAcc>
                        {openAccordion === item?.topic?.id &&
                            item.topic.lectures.map((lecture) => (
                                <LectureItem 
                                    key={lecture.video || lecture.id} 
                                    lecture={lecture}
                                    checked={checked}
                                    setChecked={setChecked}
                                    setVideo={setVideo}
                                    setIdTopic={setIdTopic}
                                    t={t}
                                    i18n={i18n}
                                    translateText={translateText}
                                    isParentOpen={openAccordion === item.topic.id}
                                />
                            ))
                        }
                    </div>
                ))}
            </div>
        </div>
    );
};

const LectureItem = ({ lecture, checked, setChecked, setVideo, setIdTopic, t, i18n, translateText, isParentOpen }) => {
    const [translatedTitle, setTranslatedTitle] = useState(lecture.title);

    useEffect(() => {
        const translateLectureTitle = async () => {
            if (i18n.language === 'en') {
                setTranslatedTitle(lecture.title);
                return;
            }
            
            try {
                const translated = await translateText(lecture.title, i18n.language);
                setTranslatedTitle(translated);
            } catch (error) {
                console.error('Translation error:', error);
                setTranslatedTitle(lecture.title);
            }
        };
        
        if (isParentOpen || i18n.language !== 'en') {
            translateLectureTitle();
        }
    }, [lecture.title, i18n.language, isParentOpen]);

    return (
        <div 
            onClick={() => {
                if (lecture.video) {
                    setChecked(lecture.video);
                }
                setVideo(lecture.video);
                setIdTopic(lecture);
            }}
            className={`p-4 flex gap-2 ${checked === lecture.video || checked === lecture.id ? 'bg-[#FFE6E6]' : 'bg-[#FFF]'}`}
        >
            <label className="flex cursor-pointer">
                <input 
                    type="checkbox" 
                    onChange={() => setChecked(lecture.video ? lecture.video : lecture.id)} 
                    className="hidden"
                />
                <div className='w-[24px] h-[24px]'>
                    {checked === lecture.video || checked === lecture.id ? <Check /> : <NoCheck />}
                </div>
            </label>
            <div className='grid gap-1 w-full'>
                <div className="text-sm flex items-center justify-between w-full">
                    <span className='text-[16px] mb-[6px]'>
                        {t('sidebar.lecture')}: {translatedTitle}
                    </span>
                    {lecture.video && (
                        <Button 
                            size='small' 
                            className='!text-[#1E1E1E4D] flex items-center gap-1 rounded-lg mb-[6px]'
                        >
                            <Video size={20} />
                            {Math.round(lecture.duration)} {t('time.minutes')}
                        </Button>
                    )}
                </div>
                <div className="text-sm flex items-center gap-[8px]">
                    <p className='text-[14px]'>{t('sidebar.material')}:</p>
                    <div className={`flex items-center gap-1 rounded-lg ${checked === lecture.video || checked === lecture.id ? "bg-[#FFF]" : "bg-[#BA7373]"} px-2 py-1`}>
                        {lecture.video ? (
                            <button className={`${checked === lecture.video || checked === lecture.id ? "text-black" : "text-white"}`}>
                                {t('sidebar.start_video')}
                            </button>
                        ) : (
                            <>
                                <div>
                                    <FileWord />
                                </div>
                                <Link 
                                    target={"_blank"} 
                                    to={lecture.material} 
                                    download 
                                    className={`${checked === lecture.video || checked === lecture.id ? "text-[#000]" : "text-[#FFF]"}`}
                                >
                                    {lecture.material_name}
                                </Link>
                            </>
                        )}
                    </div>
                </div>
                {lecture.teacher_name && (
                    <p className='text-black flex items-center gap-1 text-[14px]'>
                        {t('sidebar.teacher')}: {lecture.teacher_name}
                    </p>
                )}
            </div>
        </div>
    );
};

export default SideBar;